import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "../LangContext";
import { useAuth } from "../AuthContext";
import { C } from "../../lib/colors";
import { GUIDE_CONTENT } from "./guideContent";
import GuideBody from "./GuideBody";

// ─── Fase 14 · Bloque 8 — Public /guide page ──────────────────────────────
// SEO-friendly route. Fase 17B · Bloque 2: content is now rendered by the
// shared `<GuideBody />`; this file only owns the page header, the closing
// CTA and the SEO meta injection.
export default function Guide() {
  const { lang, setLang } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const content = GUIDE_CONTENT[lang] || GUIDE_CONTENT.es;

  // Inject SEO into <head>
  useEffect(() => {
    const prevTitle = document.title;
    document.title = content.seo.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", content.seo.description);
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}/guide`);
    return () => {
      document.title = prevTitle;
      if (prevDesc) metaDesc.setAttribute("content", prevDesc);
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [content]);

  const handleCta = () => {
    if (user) navigate("/app");
    else navigate("/");
  };

  return (
    <div data-testid="guide-page" style={{ minHeight: "100vh", background: C.ink, color: C.linen,
                                             paddingBottom: 80 }}>
      <header style={{ padding: "20px 24px", borderBottom: `1px solid ${C.dust}`,
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        gap: 12, flexWrap: "wrap" }}>
        <Link to="/" data-testid="guide-home-link"
              style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 12,
                        letterSpacing: 0.25, textDecoration: "none" }}>
          ← Prop Forge
        </Link>
        <LangToggle lang={lang} setLang={setLang} />
      </header>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px" }}>
        <GuideBody />

        {/* Closing + CTA */}
        <div style={{ marginTop: 56, padding: "24px",
                       borderTop: `1px solid ${C.brass}`, background: `${C.brass}08` }}>
          <p style={{ fontFamily: "var(--plex)", fontSize: 14, lineHeight: 1.65,
                       color: C.linen, margin: "0 0 18px" }}>
            {content.closing}
          </p>
          <button data-testid="guide-cta"
                  onClick={handleCta}
                  className="fg-btn-ghost"
                  style={{ fontSize: 14, padding: "10px 22px",
                            color: C.brass, border: `1px solid ${C.brass}`,
                            background: "transparent", cursor: "pointer",
                            fontFamily: "var(--mono)", letterSpacing: 0.2 }}>
            → {user ? content.cta.loggedIn : content.cta.loggedOut}
          </button>
        </div>
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div data-testid="guide-lang-toggle" style={{ display: "flex", gap: 4 }}>
      {["es", "en"].map(l => (
        <button key={l} onClick={() => setLang(l)}
                data-testid={`guide-lang-${l}`}
                style={{ background: "none", border: `1px solid ${lang === l ? C.brass : C.dust}`,
                          color: lang === l ? C.brass : C.smoke,
                          padding: "4px 10px", cursor: "pointer",
                          fontFamily: "var(--mono)", fontSize: 11, letterSpacing: 0.15 }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
